import {
  Box,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface IRadio<T extends any> {
  label: string;
  value: T;
}

interface IRadioGoupInputProps<T extends any> {
  name: string;
  groupLabel: string;
  radios: IRadio<T>[];
  disableBorder?: boolean;
  inline?: boolean;
  fullwidth?: boolean;
  valueField?: keyof T;
  disabled?: boolean;
  icon?: JSX.Element;
  onIconClick?: (value: T) => void;
}

const RadioGroupInputIcon = <T extends any>({
  name,
  radios,
  groupLabel,
  disableBorder = false,
  valueField,
  inline = false,
  fullwidth = false,
  disabled = false,
  icon,
  onIconClick,
}: IRadioGoupInputProps<T>) => {
  const { control } = useFormContext();
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<string | null>(null);
  const defaultValue = radios[0];

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue ? defaultValue.value : null}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
        <Box
          sx={(theme) => ({
            paddingBlock: disableBorder ? 0 : 1,
            paddingInline: disableBorder ? 0 : 2,
            border: disableBorder
              ? undefined
              : theme.palette.mode === "light"
              ? `1px solid ${theme.palette.grey[500]}`
              : `1px solid ${theme.palette.grey[400]}`,
            borderRadius: 2,
          })}
        >
          <FormLabel>{groupLabel}</FormLabel>
          <RadioGroup>
            <Stack
              sx={{
                width: fullwidth ? "100%" : undefined,
                display: "flex",
                flexDirection: inline ? "row" : undefined,
                justifyContent: fullwidth ? "space-between" : undefined,
                flexWrap: "wrap",
              }}
            >
              {radios.map((radio) => (
                <Box
                  key={radio.label}
                  sx={{ display: "flex", alignItems: "center" }}
                  onMouseEnter={() => setHovered(radio.label)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <FormControlLabel
                    value={radio.value}
                    disabled={disabled}
                    control={
                      <Radio
                        size="small"
                        checked={
                          value
                            ? valueField
                              ? value[valueField] === radio.value[valueField]
                              : radio.value === value
                            : !!(radio.value as any).main
                        }
                        onChange={() => {
                          onChange(radio.value);
                        }}
                      />
                    }
                    label={radio.label}
                    ref={ref}
                  />
                  {icon &&
                    (hovered === radio.label ||
                      !!(radio.value as any).main) && (
                      <IconButton
                        size="small"
                        onClick={() => onIconClick && onIconClick(radio.value)}
                      >
                        {icon}
                      </IconButton>
                    )}
                </Box>
              ))}
            </Stack>
            <Typography
              variant="body2"
              color={(theme) => theme.palette.error.main}
            >
              {error ? t(error?.message || "") : ""}
            </Typography>
          </RadioGroup>
        </Box>
      )}
    />
  );
};

export default RadioGroupInputIcon;
